/**
 * The single source of TanStack Query keys.
 *
 * Every key is hierarchical — `['transactions']` is a prefix of
 * `['transactions', 'list', filters]` — so invalidating a resource root
 * invalidates its lists and details, and a mutation can invalidate the
 * narrowest key that covers what it changed.
 *
 * Call sites import from here. An inline array in a component is the thing
 * this file exists to prevent: it drifts from the key the mutation
 * invalidates, and the symptom is a stale panel rather than an error.
 */

/**
 * `F` is explicitly defaulted rather than left to be inferred. There is no
 * inference site for it — `resource('accounts')` passes no value of type `F` —
 * so an unparameterised call resolves it to the default, and a resource whose
 * filters are known can pin them with `resource<{ page: number }>('…')` as the
 * modules land.
 */
function resource<F extends Record<string, unknown> = Record<string, unknown>>(name: string) {
  return {
    all: () => [name] as const,
    list: (filters?: F) => [name, 'list', filters ?? {}] as const,
    detail: (id: string) => [name, 'detail', id] as const,
  }
}

export const keys = {
  user: { all: () => ['user'] as const, me: () => ['user', 'me'] as const },
  /** M13: `GET/PATCH /users/me/notification-preferences` — its own record, not part of the profile. */
  notificationPreferences: {
    all: () => ['notification-preferences'] as const,
    /** Identifies the three writers so `isMutating` can tell whether another save is still in flight. */
    write: () => ['notification-preferences', 'write'] as const,
  },
  accounts: { ...resource('accounts'), statementDayWrite: () => ['accounts', 'statement-day-write'] as const },
  /**
   * `summary` is M5 Ticket 4's `/transactions/summary` read (`useAccountSummary
   * .ts`) — the detail hero's "In {month}". Scoped by account id AND the two
   * date bounds, not folded into `.list()`'s filter bag: it is a distinct
   * resource shape (one aggregate object, not a page of transactions) and
   * every account/month combination is its own cache entry, the same way
   * `recurring.calendar(month, year)` scopes by both of its own axes.
   */
  transactions: {
    ...resource('transactions'),
    summary: (accountId: string, start: string, end: string) =>
      ['transactions', 'summary', accountId, start, end] as const,
    /**
     * M6's "N to review" count — its own key rather than a `list()` with a
     * `review_status` filter, because its payload is one number, not a page of
     * rows, and two shapes under one key is a cache that lies. Under the
     * `transactions` root, so any transaction mutation invalidates it.
     */
    reviewCount: (month: string | null) => ['transactions', 'review-count', { month }] as const,
    /** M6's merchant stats: rows of one merchant this year, folded client-side. Under the `transactions` root, so an edit invalidates it. */
    merchantStats: (merchantId: string, year: number) => ['transactions', 'merchant-stats', merchantId, year] as const,
    /** The distinct tag values — the "More filters" tag picker. Its own key: the payload is a list of strings. */
    tags: () => ['transactions', 'tags'] as const,
    /** One tag's usage, `total` of `GET /transactions?tag=`. Under the root so any transaction or tag mutation invalidates it. */
    tagCount: (name: string) => ['transactions', 'tag-count', name] as const,
    /**
     * M5's credit-card statement cycles. Under the `transactions` root because
     * cycles are derived from transaction rows: any transaction mutation that
     * invalidates the root refreshes them for free.
     */
    statementCycles: (accountId: string, tz: string, count: number) =>
      ['transactions', 'statement-cycles', accountId, { tz, count }] as const,
    /** Every tz/count entry of one account — what a statement-day change invalidates. */
    statementCyclesOf: (accountId: string) => ['transactions', 'statement-cycles', accountId] as const,
  },
  /**
   * The merchant picker's "Most used" (`GET /merchants?sort=most_used`). Its OWN root, NOT under
   * `merchants`: usage is computed per request and never cached (`gcTime: 0`), and a merchant edit
   * invalidates it explicitly.
   */
  merchantUsage: {
    all: () => ['merchant-usage'] as const,
    mostUsed: () => ['merchant-usage', 'most-used'] as const,
  },
  /**
   * M6's review inbox. Its own root, NOT under `transactions`: an autosaved category change
   * invalidates `transactions/list`, and the review screen holds its rows where they are until
   * Done (a real change moves a row out of `pending` on the server, and a refetch would make it
   * jump). A mutation that really changes what is pending invalidates `review` explicitly.
   */
  review: {
    all: () => ['review'] as const,
    summary: () => ['review', 'summary'] as const,
    pending: () => ['review', 'pending'] as const,
  },
  /**
   * M3's `/reports/cashflow`. `details` is part of the key: a free body has no premium sections,
   * so a free→premium change must be a different entry and refetch, never serve the free body.
   * `scope` is present only on a drill-down call (absent, not null, on the unscoped ones, so their keys are unchanged): two scopes never share an entry.
   */
  cashflow: {
    all: () => ['cashflow'] as const,
    /** The ten-year bucket series behind the chart and the table. */
    history: (p: { granularity: string; start: string; end: string; tz: string; scope?: Readonly<Record<string, string>> }) => ['cashflow', 'history', p] as const,
    /**
     * M8 Reports on `GET /reports/query`. Under `cashflow`, so a cashflow invalidation reaches it. The chart type is
     * NOT part of the key (M8-R2). `limit` is: the table (100) and the chart (4) are two entries.
     */
    query: (p: Readonly<Record<string, unknown>>) => ['cashflow', 'query', p] as const,
    /** One selected period: totals, prior, and the breakdown when `details`. */
    period: (p: { granularity: string; start: string; end: string; tz: string; details: boolean; scope?: Readonly<Record<string, string>> }) =>
      ['cashflow', 'period', p] as const,
    /** The Overview heatmap's day buckets for one month (`granularity=day`). Under `cashflow`, so a cashflow invalidation reaches it. */
    dailySpend: (month: string, currency: string, tz: string) => ['cashflow', 'daily-spend', { month, currency, tz }] as const,
  },
  /** `GET /insights`: computed on request over the cashflow pipeline. Nothing invalidates it; it refetches once the app-wide 30s `staleTime` has lapsed (a remount inside that window serves the cache). */
  insights: {
    all: () => ['insights'] as const,
    list: (p: { tz: string; limit: number }) => ['insights', 'list', p] as const,
  },
  budgets: resource('budgets'),
  recurring: {
    ...resource('recurring'),
    summary: () => ['recurring', 'summary'] as const,
    calendar: (month: number, year: number) => ['recurring', 'calendar', { month, year }] as const,
    /** Every month's calendar: the prefix a write that moves an entry reaches. */
    calendars: () => ['recurring', 'calendar'] as const,
    /** A sibling of `detail(id)`, not nested under it: a write must invalidate the `['recurring']` root. */
    payments: (id: string) => ['recurring', 'payments', id] as const,
    suggestions: () => ['recurring', 'suggestions'] as const,
    /** The pending list's pages, under `suggestions()` so one invalidation reaches both reads. */
    suggestionsPending: () => ['recurring', 'suggestions', 'pending'] as const,
    /** The Suggestions button's badge: a one-row read whose payload is the total. */
    suggestionsCount: () => ['recurring', 'suggestions', 'count'] as const,
    /** A MUTATION key: one accept per suggestion, locked in the client's mutation cache so it outlives the row. */
    suggestionAccept: (id: string) => ['recurring', 'suggestion-accept', id] as const,
    /** A MUTATION key: one dismiss per suggestion. */
    suggestionDismiss: (id: string) => ['recurring', 'suggestion-dismiss', id] as const,
    /**
     * A MUTATION key, not a query key: the mark-paid in-flight lock (D8, S8).
     * The pending mutation lives in the client's mutation cache, so the lock
     * outlives the row that started it (Review Focus 1).
     */
    markPaid: (id: string) => ['recurring', 'mark-paid', id] as const,
    /** A MUTATION key: skip, pause/resume and delete share one lock per bill (one write at a time, S13). */
    lifecycle: (id: string) => ['recurring', 'lifecycle', id] as const,
    /**
     * A MUTATION key: link, unlink and "Find past payments" share one lock per
     * bill. Each moves which transaction settles which period, so a second one
     * while the first is out is refused, never queued (Task 10).
     */
    links: (id: string) => ['recurring', 'links', id] as const,
    /**
     * A MUTATION key: the add/edit form's save (Task 11). `'new'` for a create,
     * so a remounted form still sees its own pending save as `saving`.
     */
    save: (kind: 'create' | 'edit' | 'fromTransaction', id: string | null) => ['recurring', 'save', kind, id ?? 'new'] as const,
  },
  categories: resource('categories'),
  /**
   * The icon catalogue behind the category form's picker (#447). Its OWN
   * root, not nested under `categories`: every category write invalidates
   * `categories/list`, and none of them changes the catalogue — it changes
   * only when a backend migration ships one. `group: null` (every group) is a
   * distinct entry from any named group, and each search query is its own.
   */
  categoryIcons: {
    all: () => ['category-icons'] as const,
    groups: () => ['category-icons', 'groups'] as const,
    browse: (group: string | null) => ['category-icons', 'browse', { group }] as const,
    search: (q: string) => ['category-icons', 'search', { q }] as const,
  },
  merchants: {
    ...resource('merchants'),
    /**
     * The merchant picker's pages — an INFINITE query, so never a `list()` entry: two shapes under one
     * key is a cache that lies. Under the `merchants` root, so a create invalidates it. `search: ''` is the A–Z browse.
     */
    picker: (search: string) => ['merchants', 'picker', { search }] as const,
    /**
     * The brand catalogue (#495, #494): `GET /merchant-catalogue/search` and `/match`. Under the `merchants`
     * root on purpose: the server hides brands the user already has, so creating, sharing or deleting a
     * merchant must refetch both, and every merchant write already invalidates `merchants`.
     */
    brandSearch: (q: string) => ['merchants', 'brands', 'search', { q }] as const,
    brandMatch: (name: string) => ['merchants', 'brands', 'match', { name }] as const,
    /** Whether a merchant the user can see (a global one included) already carries a brand's name. */
    brandListed: (name: string) => ['merchants', 'brands', 'listed', { name }] as const,
  },
  rules: {
    ...resource('rules'),
    /** An UNSAVED rule's dry run, keyed on the body that was previewed. Under
     *  the `rules` root so any rule mutation invalidates it. */
    preview: (body: unknown) => ['rules', 'preview', body] as const,
    /** One rule's application log ("Applied to"), an INFINITE query, so never a
     *  `list()` entry. Under the `rules` root so a batch Undo, which
     *  invalidates it, refreshes it. */
    logs: (ruleId: string) => ['rules', 'logs', ruleId] as const,
    /** Whether the rule has EVER changed anything, undone or not: one row of its unfiltered
     *  log. Tells "Not yet" from "All changes undone" when nothing is in effect. */
    logsEver: (ruleId: string) => ['rules', 'logs-ever', ruleId] as const,
    /** Which rule set a field on one transaction: its unreverted logs (`/rule-logs?transaction_id=`).
     *  Under the `rules` root so an apply, a Remove or an Undo refreshes it. */
    forTransaction: (transactionId: string) => ['rules', 'for-transaction', transactionId] as const,
    /** "Matches now", an INFINITE query over `/rules/{id}/matches` (cursor-paged). */
    matches: (ruleId: string) => ['rules', 'matches', ruleId] as const,
    /** "Removed", an INFINITE query over `/rules/{id}/exclusions`. */
    exclusions: (ruleId: string) => ['rules', 'exclusions', ruleId] as const,
    /** Apply now's dry run for one window and overwrite choice. */
    applyPreview: (ruleId: string, p: { from: string; overwrite: boolean; resumeAfter: string | null }) => ['rules', 'apply-preview', ruleId, p] as const,
  },
  /** Suggested rules — a separate resource from saved rules (Task 11). */
  ruleSuggestions: resource('rule-suggestions'),
  /**
   * M12-R14: `householdId` is threaded through every household-scoped key even
   * though v1 resolves it implicitly. Collapsing it out is the change that
   * would make multi-household a refactor rather than an addition — and the
   * backend already returns more than one household per user, so "implicit"
   * means "resolved once", not "does not exist".
   */
  household: {
    ...resource('household'),
    members: (householdId: string) => ['household', 'members', householdId] as const,
    invitations: (householdId: string) => ['household', 'invitations', householdId] as const,
  },
  notifications: resource('notifications'),
  /** M13 Settings › Security — `GET /mfa/status` + `/backup-codes`: the recovery-code count and the email-factor flag. Never the codes themselves. */
  mfa: {
    all: () => ['mfa'] as const,
    status: () => ['mfa', 'status'] as const,
    /**
     * A time in ms, set client-side (never fetched, never the code): when the last email code sent in this
     * tab stops working. The backend reports no pending code. Cleared on sign-out and user switch (`clear()`);
     * a reload empties it. Held with `gcTime: Infinity` — see `useEmailCodeExpiry`.
     */
    emailCodeExpiresAt: () => ['mfa', 'emailCodeExpiresAt'] as const,
  },
  /** M13 Settings › Devices — `GET /devices`. Read-only in v1. */
  devices: { all: () => ['devices'] as const, list: () => ['devices', 'list'] as const },
  /**
   * M5 Ticket 3. `providers` is keyed on `region` — a different catalogue per
   * region, never invalidated by a mutation (the backend's own registry
   * changes, not anything this client writes) so it carries no `.list()`/
   * `.detail()` shape from `resource()`. `items` is the aggregator's own
   * connections list (`GET /aggregator/items`), invalidated by link/reauth
   * completion and by `useSyncItem`'s existing sync mutation.
   */
  aggregator: {
    all: () => ['aggregator'] as const,
    // `null` is a real, distinct key — "let the server resolve it" caches
    // separately from any explicit region, which is correct: they can
    // legitimately answer differently for the same user.
    providers: (region: string | null) => ['aggregator', 'providers', region] as const,
    items: () => ['aggregator', 'items'] as const,
    /** `GET /aggregator/items/{id}/relink` — the match state of one reconnect (the NEW item's id). */
    relink: (itemId: string) => ['aggregator', 'relink', itemId] as const,
  },
}
